import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const EASE = [0.22, 1, 0.36, 1];

/*
 * Scroll-triggered visibility that does not depend on IntersectionObserver.
 * whileInView can leave content permanently hidden on some mobile browsers;
 * scroll/resize events fire everywhere (including with Lenis smooth scroll).
 * One shared listener keeps it cheap on low-end devices.
 */
const subscribers = new Set();
let listening = false;
const checkAll = () => subscribers.forEach((cb) => cb());
const subscribe = (cb) => {
  subscribers.add(cb);
  if (!listening) {
    listening = true;
    window.addEventListener("scroll", checkAll, { passive: true });
    window.addEventListener("resize", checkAll);
  }
  return () => subscribers.delete(cb);
};

const useScrolledIntoView = (ref, margin = 70) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return undefined;
    const check = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight - margin && r.bottom > -margin) setVisible(true);
    };
    check();
    const unsub = subscribe(check);
    const t1 = setTimeout(check, 600);
    const t2 = setTimeout(check, 1800);
    return () => { unsub(); clearTimeout(t1); clearTimeout(t2); };
  }, [visible, ref, margin]);
  return visible;
};

export const Reveal = ({ children, delay = 0, y = 30, className = "" }) => {
  const ref = useRef(null);
  const shown = useScrolledIntoView(ref);
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={shown ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

export const MaskedLine = ({ children, delay = 0, className = "", innerClassName = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className={`block ${innerClassName}`}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 1.1, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export const Stagger = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const shown = useScrolledIntoView(ref, 60);
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={shown ? "show" : undefined}
      variants={{ hidden: {}, show: { transition: { staggerChildren: 0.09, delayChildren: delay } } }}
    >
      {children}
    </motion.div>
  );
};

export const StaggerItem = ({ children, className = "" }) => (
  <motion.div
    className={className}
    variants={{
      hidden: { opacity: 0, y: 26 },
      show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
    }}
  >
    {children}
  </motion.div>
);
